/**
 * ---------------------------------------------------------------------------------------------------------------------
 * KC CART — v27: no cart-page round trips
 * ---------------------------------------------------------------------------------------------------------------------
 * The trash icon and the theme's +/- controls are plain <a href="/cart/change?..."> links. Left alone the browser
 * follows them, Shopify applies the change and redirects to /cart — which this theme retires, so the shopper sees the
 * "Taking you back to your cart…" holding screen and is bounced home on every single add or remove.
 *
 * This file intercepts those links in the capture phase, ahead of the theme's own <line-item-quantity> delegate so the
 * work is never done twice, and applies the change over AJAX using the same contract Prestige uses itself:
 * cart:prepare-bundled-sections -> /cart/change.js -> cart:change. The row also answers the tap immediately instead of
 * waiting for the network, which is what makes it feel quick.
 *
 * Deliberately self-contained: it talks to the engine in kc-cart-drawer.js only through window.KCCart, so neither file
 * has to know about the other's internals.
 * ---------------------------------------------------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__kcCartV27) return;
  window.__kcCartV27 = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_CHANGE = /\/cart\/change(\/|$|\?)/;

  /* ------------------------------------------------------------------------------------------------------------------
   * The Prestige cart contract
   * --------------------------------------------------------------------------------------------------------------- */

  function loading(state) {
    document.documentElement.dispatchEvent(
      new CustomEvent(state ? 'theme:loading:start' : 'theme:loading:end', { bubbles: true })
    );
  }

  /** Ask every listening section which of them needs re-rendering with this response. */
  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function announce(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'line-item:change', cart: cart }
      })
    );
  }

  async function changeCart(payload) {
    var body = Object.assign({}, payload, { sections: bundledSections().join(',') });

    var response = await fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    var cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || 'Cart request failed');

    announce(cart);
    return cart;
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Link interception
   * --------------------------------------------------------------------------------------------------------------- */

  function cartLink(node) {
    if (!node || !node.closest) return null;
    var anchor = node.closest('a[href]');
    if (!anchor) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    return CART_CHANGE.test(anchor.getAttribute('href') || '') ? anchor : null;
  }

  function rowFor(element) {
    return element.closest('line-item') || element.closest('.line-item');
  }

  async function applyChange(anchor, payload) {
    var row = rowFor(anchor);
    var stepper = anchor.closest('.kc-line-qty') || anchor.closest('.quantity-selector');

    // Immediate feedback. A removal collapses the row on the spot; a quantity change greys the stepper so a second
    // tap cannot race the first.
    if (payload.quantity === 0 && row) row.classList.add('is-kc-removing');
    if (stepper) stepper.classList.add('is-kc-pending');
    anchor.classList.add('is-kc-pending');

    loading(true);
    try {
      await changeCart(payload);
    } catch (e) {
      // Put the row back rather than leaving a ghost — the cart never actually changed.
      if (row) row.classList.remove('is-kc-removing');
    } finally {
      loading(false);
      if (stepper) stepper.classList.remove('is-kc-pending');
      anchor.classList.remove('is-kc-pending');
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var anchor = cartLink(event.target);
      if (!anchor) return;

      var url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch (e) {
        return;
      }

      var quantity = parseInt(url.searchParams.get('quantity'), 10);
      if (isNaN(quantity)) return;

      // /cart/change.js addresses a row either by its key (the +/- controls) or by its 1-based index (url_to_remove,
      // which is what the trash icon uses).
      var key = anchor.getAttribute('data-kc-line-key') || url.searchParams.get('id');
      var line = url.searchParams.get('line');
      var payload;

      if (key) {
        payload = { id: key, quantity: quantity };
      } else if (line) {
        payload = { line: parseInt(line, 10), quantity: quantity };
      } else {
        return;
      }

      // Stop here: the theme's line-item-quantity delegate listens on the bubble phase and would otherwise fire its
      // own fetch for the same click — and on /cart it would navigate instead.
      event.preventDefault();
      event.stopPropagation();

      applyChange(anchor, payload);
    },
    true
  );

  /**
   * Typing a figure into the quantity input is handled by the theme, which on /cart does a full navigation. /cart is
   * retired here, so that path has to be closed too.
   */
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('input[data-line-key]')) return;
      if (!input.closest('#cart-drawer, .cart-drawer')) return;

      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) return;

      event.stopPropagation();

      var row = rowFor(input);
      if (quantity === 0 && row) row.classList.add('is-kc-removing');

      loading(true);
      changeCart({ id: input.getAttribute('data-line-key'), quantity: quantity })
        .catch(function () {
          if (row) row.classList.remove('is-kc-removing');
        })
        .then(function () {
          loading(false);
        });
    },
    true
  );
})();
